import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Truck,
  FileText,
  Clock,
  Package,
  CheckCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import goodsReceiptNoteService from '../../services/goodsReceiptNoteService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const GoodsReceiptNote = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalGRNs: 0,
    draftGRNs: 0,
    receivedGRNs: 0,
    partiallyReceivedGRNs: 0,
    acceptedGRNs: 0,
    rejectedGRNs: 0,
    totalAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [grnsResponse, statsResponse] = await Promise.all([
        goodsReceiptNoteService.getAllGRNs(),
        goodsReceiptNoteService.getGRNStats()
      ])
      
      if (grnsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (grnsResponse.data || []).map(grn => ({
          ...grn,
          id: grn._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (grnsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No GRN records found. Create your first goods receipt note!')
        }
      } else {
        toast.error(grnsResponse.message || 'Failed to load GRNs')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading GRN records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view GRNs')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load GRN records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Received': 'pending',
      'Partially Received': 'warning',
      'Quality Check Pending': 'pending',
      'Quality Check Passed': 'active',
      'Quality Check Failed': 'danger',
      'Accepted': 'active',
      'Rejected': 'danger',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'grnNumber',
      header: 'GRN Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'purchaseOrderNumber',
      header: 'PO Number',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'receiptDate',
      header: 'Receipt Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'receivedByName',
      header: 'Received By',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'receiptSummary.totalItemsAccepted',
      header: 'Items Accepted',
      cell: ({ row }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {row.original.receiptSummary?.totalItemsAccepted || 0} / {row.original.receiptSummary?.totalItemsOrdered || 0}
        </div>
      ),
    },
    {
      accessorKey: 'receiptSummary.totalAmount',
      header: 'Total Amount',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(row.original.receiptSummary?.totalAmount || 0, row.original.receiptSummary?.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/goods-receipt-note/form')
  }

  const handleEdit = (grnData) => {
    navigate('/procurement/goods-receipt-note/edit', { state: { grnData } })
  }

  const handleView = (grnData) => {
    navigate('/procurement/goods-receipt-note/view', { state: { grnData } })
  }

  const handleDelete = (grnData) => {
    setDeleteItem(grnData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting GRN...')
      const response = await goodsReceiptNoteService.deleteGRN(deleteItem._id)
      if (response.success) {
        toast.success('GRN deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete GRN')
      }
    } catch (error) {
      console.error('Error deleting GRN:', error)
      if (error.response?.status === 404) {
        toast.error('GRN not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Can only delete draft GRNs')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this GRN')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete GRN. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Transactions', path: '/procurement?tab=transactions' },
    { label: 'Receipts', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Receipts</h1>
          <p>Goods received and delivery confirmations</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Receipt
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Truck size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalGRNs || 0}</div>
          <div className="metric-title">Total Receipts</div>
          <div className="metric-subtitle">All GRN records</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftGRNs || 0}</div>
          <div className="metric-title">Draft</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Received</span>
            </div>
          </div>
          <div className="metric-value">{stats.receivedGRNs || 0}</div>
          <div className="metric-title">Received</div>
          <div className="metric-subtitle">Goods logged</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Package size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Partial</span>
            </div>
          </div>
          <div className="metric-value">{stats.partiallyReceivedGRNs || 0}</div>
          <div className="metric-title">Partially Received</div>
          <div className="metric-subtitle">Open receipts</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Accepted</span>
            </div>
          </div>
          <div className="metric-value">{stats.acceptedGRNs || 0}</div>
          <div className="metric-title">Accepted</div>
          <div className="metric-subtitle">Quality approved</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalAmount || 0, 'INR')}</div>
          <div className="metric-title">Total Amount</div>
          <div className="metric-subtitle">All receipts combined</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="goods-receipt-note-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete GRN"
        message={`Are you sure you want to delete GRN "${deleteItem?.grnNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default GoodsReceiptNote

